import {
    Box,
    Text,
    AspectRatio,
    Image,
    HStack,
    Divider, CircularProgress,
} from '@chakra-ui/react'
import React, {useState, KeyboardEvent, MouseEvent, useEffect} from 'react'
import {useFormContext} from 'react-hook-form'
import {isNumber} from 'lodash-es'
import {useTranslation} from 'next-i18next'
import Select from '_app/components/Form/Select'
import InputWithCounter from '_app/components/Form/InputWithCounter'
import {EngraveTabComponentProps} from '_app/types/product'
import {engraveOptionName} from 'modules/product/utils/engraveOptionName'
import getHTMLInputElementCaretPosition from '_app/utils/getHTMLInputElementCaretPosition'
import theme from 'theme'
import {Button} from '_app/components'
import getCurrency from '_app/utils/localized/getCurrency'
import PriceText from '_app/components/PriceText'
import {useRouter} from '_app/hooks/useRouter'
import {getPaintingExample} from './helpers'

const SPECIAL_CHARACTERS = ['♡']

const Initials = ({
    option,
    onAddOption,
    buttonProps,
}: EngraveTabComponentProps) => {
    const {t} = useTranslation('engraving')
    const {locale} = useRouter()
    const {getValues, setValue} = useFormContext()
    const [loading, setLoading]: any = useState(true)
    const [caretPosition, setCaretPosition] = useState<[number, number]>([0, 0])
    const [valueInput, setValueInput]: any = useState('')
    const [examplePainting, setExamplePainting] = useState<{ mobile: string; desktop: string; }>({
        mobile: '',
        desktop: ''
    })
    const maxCharacters =
        option?.value?.max_characters && option?.value?.max_characters > 0 ? option.value.max_characters : undefined
    const baseName = `${engraveOptionName(option)}`
    const valueName = `${baseName}.value`
    const handleChangeExamplePainting = (val: any) => {
        setExamplePainting(getPaintingExample(val?.value))
    }
    const onAddChar = (char: string) => {
        const value = getValues(valueName) || ''
        const [start, stop] = caretPosition
        const newValue = `${value.substring(0, start)}${char}${value.substring(
            stop,
            value.length
        )}`
        if (isNumber(maxCharacters) && newValue.length > maxCharacters) {
            return
        }
        setCaretPosition([start + char.length, stop + char.length])
        setValue(valueName, newValue)
        setValueInput(newValue)
    }

    const inputKeyUpHandler = (e: any) => {
        const value: any = e?.target?.value
        const regexRule = /[^a-zą-źĄ-ŹA-Z♡łŁ]/g
        const regex = new RegExp(regexRule)
        const position = getHTMLInputElementCaretPosition(
            e?.nativeEvent?.target as HTMLInputElement
        )
        if (!position) return
        if (maxCharacters && !regex?.test(value) && value?.length <= maxCharacters && value?.length >= 0) {
            setValue(`${baseName}.value`, value.replace(regexRule, '').substring(0, maxCharacters))
            setValueInput(value.replace(regexRule, '').substring(0, maxCharacters))
            setCaretPosition(position)
        }
    }
    const inputClickHandler = (
        e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>
    ) => {
        const position = getHTMLInputElementCaretPosition(
            e?.nativeEvent?.target as HTMLInputElement
        )
        if (!position) return
        setCaretPosition(position)
    }
    useEffect(() => {
        if (option) {
            setTimeout(() => {
                const defaultColor = {value: 'GOLD'}
                setValue('engrave_options[1].front.malowanie_1.options.color', defaultColor?.value || '')
                setLoading(false)
            }, 5000)
        }
    }, [option, onAddOption])
    useEffect(() => {
        setExamplePainting(getPaintingExample('GOLD'))
    }, [])
    return (
        <Box>
            <>
                <Text mb={3} fontWeight={300} flexDirection="row" display="flex">
                    {t('INITIALS_PRICE')}:
                    {option.value?.price && (
                        <Text pl={1} fontWeight={400}>
                            <PriceText
                                amount={{
                                    value: option.value.price,
                                    currency: getCurrency(locale),
                                }}
                                floating
                            />
                        </Text>
                    )}
                </Text>
                <Divider mb={6}/>
                <Select
                    id="colors-select"
                    name={`${baseName}.options.color`}
                    placeholder={t('CHOOSE_COLOR_SCHEME_PLACEHOLDER')}
                    label={t('CHOOSE_COLOR_SCHEME_LABEL')}
                    defaultValue={'GOLD'}
                    options={[
                        {
                            value: 'YELLOW_WITH_WHITE',
                            label: t('YELLOW_WITH_WHITE'),
                        },
                        {value: 'ROSE_WITH_GOLD', label: t('ROSE_WITH_GOLD')},
                        {
                            value: 'BLUE_WITH_ROSE',
                            label: t('BLUE_WITH_ROSE'),
                        },
                        {
                            value: 'ROSE_WITH_WHITE',
                            label: t('ROSE_WITH_WHITE'),
                        },
                        {
                            value: 'SKY_BLUE_WITH_GOLD',
                            label: t('SKY_BLUE_WITH_GOLD'),
                        },
                        {
                            value: 'ORANGE_WITH_PINK',
                            label: t('ORANGE_WITH_PINK'),
                        },
                        {
                            value: 'ROSE_WTH_SKY_BLUE',
                            label: t('ROSE_WTH_SKY_BLUE'),
                        },
                        {
                            value: 'RED_WITH_BLUE',
                            label: t('RED_WITH_BLUE'),
                        },
                        {
                            value: 'TURQUOISE_WITH_YELLOW',
                            label: t('TURQUOISE_WITH_YELLOW'),
                        },
                        {
                            value: 'ORANGE_WITH_BLUE',
                            label: t('ORANGE_WITH_BLUE'),
                        },
                        {
                            value: 'RED_WITH_GOLD',
                            label: t('RED_WITH_GOLD'),
                        },
                        {
                            value: 'ROSE_WITH_RED',
                            label: t('ROSE_WITH_RED'),
                        },
                        {value: 'BLUE', label: t('BLUE')},
                        {value: 'GOLD', label: t('GOLD')},
                        {value: 'SILVER', label: t('SILVER')},
                        {value: 'BLACK', label: t('BLACK')},
                    ]}
                    setActive={(val: any) => handleChangeExamplePainting(val)}
                />

                <Box>
                    {
                        loading ? (
                            <Box>
                                <CircularProgress sx={{marginLeft: '20px', transform: 'scale(.7)'}} isIndeterminate/>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    display={{base: 'none', md: 'block'}}
                                    bg={theme.colors.lightPink}
                                >
                                    <AspectRatio ratio={570 / 298}>
                                        <Image src={examplePainting.desktop} alt="light pink desktop"/>
                                    </AspectRatio>
                                </Box>
                                <Box
                                    display={{base: 'block', md: 'none'}}
                                    bg={theme.colors.lightPink}
                                >
                                    <AspectRatio ratio={290 / 449}>
                                        <Image src={examplePainting.mobile} alt="light pink mobile"/>
                                    </AspectRatio>
                                </Box>
                                <Box
                                    mt="0.5rem"
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    gap={6}
                                    alignItems="center"
                                >
                                    <InputWithCounter
                                        name={`${baseName}.value`}
                                        label={t('WRITE_CONTENT')}
                                        placeholder={t('CONTENT')}
                                        maxCharacters={maxCharacters}
                                        length={valueInput?.length}
                                        onKeyUp={inputKeyUpHandler}
                                        onChange={inputKeyUpHandler}
                                        onClick={inputClickHandler}
                                    />
                                    <Button
                                        onClick={onAddOption}
                                        type="button"
                                        variant="outline"
                                        disabled={Boolean(examplePainting) && valueInput?.length === 0}
                                        {...buttonProps}
                                    />
                                </Box>
                                <HStack spacing="0.375rem" alignItems="center" pb={4}>
                                    <Text fontWeight={300}>{t('INSERT_SPECIAL_CHARACTER')}</Text>
                                    {SPECIAL_CHARACTERS?.map(char => (
                                        <Text
                                            key={`spc-${char}`}
                                            fontFamily="LearningCurve"
                                            fontSize="1.2em"
                                            role="button"
                                            onClick={() => onAddChar(char)}
                                        >
                                            {char}
                                        </Text>
                                    ))}
                                </HStack>
                            </>
                        )
                    }
                </Box>
                {
                    locale === 'pl' && (
                        <Box>
                            <em>
                                W przypadku wybrania odbioru produktu w butiku stacjonarnym czas realizacji usługi malowania
                                wynosi od 2 do 7 dni roboczych.
                            </em>
                        </Box>
                    )
                }
            </>
        </Box>
    )
}

export default Initials
